import { Card } from '@mui/material'
import Grid from '@mui/material/Grid'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Loader from 'examples/Loader'
import CustomDataTable from '../Users/CustomDataTable'
import { useEffect, useState } from 'react'
import { useAppServices } from 'hook/services'
import { Doughnut, Bar } from 'react-chartjs-2'

const MetricCard = ({ label, value, delta }) => (
    <Card sx={{ p: 2, height: '100%' }}>
        <MDTypography variant="caption" color="text" fontWeight="medium">
            {label}
        </MDTypography>
        <MDTypography variant="h4" fontWeight="bold" mt={0.5}>
            {value ?? '—'}
        </MDTypography>

    </Card>
)

function TermsTable({ terms }) {
    const [page, setPage] = useState(1)
    const perPage = 10
    const totalPages = Math.ceil(terms.length / perPage)
    const rows = terms.slice((page - 1) * perPage, page * perPage)
    const maxCount = terms[0]?.count || 1

    return (
        <>
            <MDBox sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                            {['#', 'Term', 'Searches', 'Share'].map((h, i) => (
                                <th key={h} style={{
                                    textAlign: i >= 2 ? 'right' : 'left',
                                    padding: '6px 8px', color: '#6b7280', fontWeight: 500,
                                    width: i === 0 ? 40 : 'auto',
                                }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => {
                            const rank = (page - 1) * perPage + i + 1
                            const share = ((row.count / maxCount) * 100).toFixed(1)
                            return (
                                <tr key={row.term} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '7px 8px', color: '#9ca3af' }}>{rank}</td>
                                    <td style={{ padding: '7px 8px', fontWeight: 500 }}>{row.term}</td>
                                    <td style={{ padding: '7px 8px', textAlign: 'right' }}>{row.count}</td>
                                    <td style={{ padding: '7px 8px', textAlign: 'right', minWidth: 110 }}>
                                        <MDBox display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                                            <div style={{ width: 60, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                                                <div style={{ width: `${share}%`, height: '100%', background: '#3B82F6', borderRadius: 3 }} />
                                            </div>
                                            <span style={{ color: '#6b7280', minWidth: 36 }}>{share}%</span>
                                        </MDBox>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </MDBox>

            {totalPages > 1 && (
                <MDBox display="flex" justifyContent="flex-end" alignItems="center" gap={1} mt={1.5}>
                    <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        style={{
                            padding: '4px 12px', borderRadius: '6px', fontSize: 12, cursor: page === 1 ? 'default' : 'pointer',
                            border: '1px solid #d1d5db', background: page === 1 ? '#f3f4f6' : '#fff',
                        }}
                    >Prev</button>
                    <MDTypography variant="caption" color="text">{page} / {totalPages}</MDTypography>
                    <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        style={{
                            padding: '4px 12px', borderRadius: '6px', fontSize: 12, cursor: page === totalPages ? 'default' : 'pointer',
                            border: '1px solid #d1d5db', background: page === totalPages ? '#f3f4f6' : '#fff',
                        }}
                    >Next</button>
                </MDBox>
            )}
        </>
    )
}


export default function SearchAnalyticsDashboard() {
    const AppService = useAppServices()

    const [loader, setLoader] = useState(true)
    const [data, setData] = useState(null)
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [dayFilter, setDayFilter] = useState('')
    const [keyword, setKeyword] = useState('')
    const [kwResult, setKwResult] = useState(null)
    const [dateErr, setDateErr] = useState('')
    const [pagination, setPagination] = useState({
        page: 1, limit: 10, totalPages: 1, total: 0,
    })

    const tableData = {
        columns: [
            { Header: 'User', accessor: 'userEmail' },
            { Header: 'Query', accessor: 'query' },
            { Header: 'Role', accessor: 'role' },
            { Header: 'Date', accessor: 'createdAt' },
        ],
        rows: (data?.recentSearches || []).map((row) => ({
            userEmail: row.userEmail || '—',
            query: row.query,
            role: (
                <span style={{
                    background: row.role === 'host' ? '#3B82F6' : '#FAEEDA',
                    color: row.role === 'host' ? '#FFFFFF' : '#854F0B',
                    padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                }}>
                    {row.role || 'guest'}
                </span>
            ),
            createdAt: new Date(row.createdAt).toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            }),
        })),
    }

    const top10 = (data?.topTerms || []).slice(0, 10)

    const barData = {
        labels: top10.map((t) => t.term),
        datasets: [{
            label: 'Searches', data: top10.map((t) => t.count),
            backgroundColor: '#3B82F6', borderRadius: 4,
        }],
    }
    const barOpts = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            x: { grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { font: { size: 11 } } },
            y: { grid: { display: false }, ticks: { font: { size: 12 } } },
        },
    }

    const doughnutData = {
        labels: ['Logged in', 'Guest'],
        datasets: [{
            data: [data?.metrics?.loggedInSearches || 0, data?.metrics?.guestSearches || 0],
            backgroundColor: ['#3B82F6', '#F59E0B'],
            borderWidth: 0,
        }],
    }
    const doughnutOpts = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: {
            legend: {
                display: true, position: 'bottom',
                labels: { font: { size: 12 }, boxWidth: 10, padding: 12 },
            },
        },
    }

    /* ── fetch ── */
    const fetchReport = async (overrides = {}, page = 1, limit = pagination.limit) => {
        setLoader(true)
        try {
            const sd = overrides.startDate ?? startDate
            const ed = overrides.endDate ?? endDate
            const day = overrides.dayFilter ?? dayFilter

            const params = new URLSearchParams()
            params.append('page', page.toString())
            params.append('limit', limit.toString())
            if (sd && ed) { params.append('startDate', sd); params.append('endDate', ed) }
            else if (day) params.append('date', day)

            const { response } = await AppService.listing.getReport({ query: params.toString() })
            if (response?.success) {
                setData(response)
                setPagination({
                    page: response.pagination?.page || page,
                    limit: response.pagination?.limit || limit,
                    totalPages: response.pagination?.totalPages || 1,
                    total: response.pagination?.total || 0,
                })
            }
        } catch (err) {
            console.error('❌ Report fetch error:', err)
        } finally {
            setLoader(false)
        }
    }

    /* ── keyword check ── */
    const checkKeyword = async () => {
        if (!keyword.trim()) return
        try {
            const params = new URLSearchParams()
            if (startDate && endDate) { params.append('startDate', startDate); params.append('endDate', endDate) }
            else if (dayFilter) params.append('date', dayFilter)
            params.append('keyword', keyword.trim())
            const { response } = await AppService.listing.getReport({ query: params.toString() })
            if (response?.keywordResult) setKwResult(response.keywordResult)
        } catch (err) {
            console.error('❌ Keyword check error:', err)
        }
    }

    const applyRange = () => {
        if (!startDate || !endDate) { setDateErr('Please select both start and end date'); return }
        if (startDate > endDate) { setDateErr('End date must be after start date'); return }
        setDateErr(''); setDayFilter('')
        fetchReport({ startDate, endDate, dayFilter: '' })
    }

    const applyDay = (val) => {
        setDayFilter(val); setStartDate(''); setEndDate(''); setDateErr('')
        fetchReport({ dayFilter: val, startDate: '', endDate: '' })
    }

    const clearAll = () => {
        setStartDate(''); setEndDate(''); setDayFilter('')
        setDateErr(''); setKwResult(null); setKeyword('')
        fetchReport({ startDate: '', endDate: '', dayFilter: '' })
    }

    const handlePageChange = (pg) => fetchReport({}, pg, pagination.limit)
    const handleLimitChange = (limit) => fetchReport({}, 1, limit)

    useEffect(() => { fetchReport() }, [])

    const m = data?.metrics
    const activeBtn = { background: '#3B82F6', color: '#fff', padding: '6px 18px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13 }
    const clearBtnSt = { padding: '6px 14px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#f3f4f6', color: '#374151', fontSize: 13, cursor: 'pointer' }

    return (
        <DashboardLayout>
            {loader ? (
                <Loader />
            ) : (
                <>
                    <DashboardNavbar />
                    <MDBox pt={6} pb={3}>
                        <Grid container spacing={6}>
                            <Grid item xs={12}>
                                <Card>

                                    {/* ── header ── */}
                                    <MDBox
                                        mx={2} py={3} px={2}
                                        variant="gradient" bgColor="info"
                                        borderRadius="lg" coloredShadow="info"
                                        display="flex" justifyContent="space-between"
                                        alignItems="center" flexWrap="wrap" gap={1}
                                    >
                                        <MDTypography variant="h6" color="white">Search Trends</MDTypography>
                                      
                                    </MDBox>

                                    <MDBox pt={3} px={3} pb={3}>

                                        <MDBox display="flex" alignItems="flex-end" flexWrap="wrap" gap={2} mb={1}>
                                            {[
                                                { label: 'Start date', val: startDate, set: (v) => { setStartDate(v); setDateErr('') } },
                                                { label: 'End date', val: endDate, set: (v) => { setEndDate(v); setDateErr('') } },
                                            ].map(({ label, val, set }) => (
                                                <MDBox key={label} display="flex" flexDirection="column" gap="4px">
                                                    <MDTypography variant="caption" color="text">{label}</MDTypography>
                                                    <input type="date" value={val} onChange={(e) => set(e.target.value)}
                                                        style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: 13 }} />
                                                </MDBox>
                                            ))}

                                            <button onClick={applyRange} style={{ ...activeBtn, height: 36 }}>Apply range</button>

                                            <button onClick={clearAll} style={clearBtnSt}>Clear</button>
                                        </MDBox>

                                        {dateErr && (
                                            <MDTypography variant="caption" color="error" mb={2} display="block">
                                                {dateErr}
                                            </MDTypography>
                                        )}

                                        <MDBox
                                            display="flex" alignItems="center" flexWrap="wrap" gap={2} mb={3} mt={1}
                                            sx={{ background: '#f9fafb', borderRadius: '8px', p: 1.5 }}
                                        >
                                            <input
                                                value={keyword}
                                                onChange={(e) => { setKeyword(e.target.value); setKwResult(null) }}
                                                onKeyDown={(e) => e.key === 'Enter' && checkKeyword()}
                                                placeholder="Check keyword e.g. brisbane , sydney ..."
                                                style={{ flex: 1, minWidth: 180, padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: 14 }}
                                            />
                                            <button onClick={checkKeyword} style={activeBtn}>Check keyword</button>

                                            {kwResult && (
                                                <MDBox display="flex" flexWrap="wrap" gap={1} alignItems="center">
                                                    {kwResult.found ? (
                                                        <>
                                                            <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#EAF3DE', color: '#3B6D11' }}>
                                                                "{kwResult.keyword}" — {kwResult.totalSearches} searches
                                                            </span>
                                                            <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#E6F1FB', color: '#185FA5' }}>
                                                                {kwResult.loggedInUsers} logged-in
                                                            </span>
                                                            <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#FAEEDA', color: '#854F0B' }}>
                                                                {kwResult.guestSearches} guest
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#FCEBEB', color: '#A32D2D' }}>
                                                            "{kwResult.keyword}" — not found in this period
                                                        </span>
                                                    )}
                                                </MDBox>
                                            )}
                                        </MDBox>

                                        {/* ── metric cards ── */}
                                        <Grid container spacing={2} mb={3}>
                                            {[
                                                { label: 'Total searches', value: m?.totalSearches?.toLocaleString(), delta: m?.deltaPercent },
                                                { label: 'Unique users', value: m?.uniqueUsers?.toLocaleString() },
                                                { label: 'Logged-in searches', value: m?.loggedInSearches?.toLocaleString() },
                                                { label: 'Guest searches', value: m?.guestSearches?.toLocaleString() },
                                            ].map((card, i) => (
                                                <Grid item xs={12} sm={6} md={3} key={i}>
                                                    <MetricCard {...card} />
                                                </Grid>
                                            ))}
                                        </Grid>

                                        {/* ── charts: bar + doughnut ── */}
                                        <Grid container spacing={3} mb={3}>
                                            <Grid item xs={12} md={8}>
                                                <Card sx={{ p: 2 }}>
                                                    <MDTypography variant="h6" mb={2}>Top searched terms</MDTypography>
                                                    <div style={{ position: 'relative', height: top10.length * 44 + 40 }}>
                                                        <Bar data={barData} options={barOpts} />
                                                    </div>
                                                    {(data?.topTerms || []).length > 10 && (
                                                        <MDBox mt={3}>
                                                            <MDTypography variant="caption" color="text" fontWeight="medium" mb={1} display="block">
                                                                All terms ({data.topTerms.length} total)
                                                            </MDTypography>
                                                            <TermsTable terms={data.topTerms} />
                                                        </MDBox>
                                                    )}
                                                </Card>
                                            </Grid>

                                            <Grid item xs={12} md={4}>
                                                <Card sx={{ p: 2 }}>
                                                    <MDTypography variant="h6" mb={2}>User type</MDTypography>
                                                    <div style={{ position: 'relative', height: 240 }}>
                                                        <Doughnut data={doughnutData} options={doughnutOpts} />
                                                    </div>
                                                    <MDBox mt={2}>
                                                        {[
                                                            { label: 'Logged in', value: m?.loggedInSearches, color: '#3B82F6' },
                                                            { label: 'Guest', value: m?.guestSearches, color: '#F59E0B' },
                                                        ].map((item) => (
                                                            <MDBox key={item.label} display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                                                <MDBox display="flex" alignItems="center" gap={1}>
                                                                    <div style={{ width: 10, height: 10, borderRadius: 2, background: item.color }} />
                                                                    <MDTypography variant="caption" color="text">{item.label}</MDTypography>
                                                                </MDBox>
                                                                <MDTypography variant="caption" fontWeight="medium">
                                                                    {item.value?.toLocaleString() ?? '—'}
                                                                </MDTypography>
                                                            </MDBox>
                                                        ))}
                                                    </MDBox>
                                                </Card>
                                            </Grid>
                                        </Grid>

                                        {/* ── recent searches table ── */}
                                        <CustomDataTable
                                            table={tableData}
                                            canSearch={false}
                                            entriesPerPage={{ defaultValue: pagination?.limit || 10, entries: [5, 10, 20] }}
                                            pagination={pagination}
                                            onPageChange={handlePageChange}
                                            onLimitChange={handleLimitChange}
                                        />

                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>
                    </MDBox>
                </>
            )}
        </DashboardLayout>
    )
}
